import { AsclepiusAppErrorCodeEnum, AuthenticationApi, type AppNameEnum } from "@vitable/asclepius-api-client-ts"
import axios, { type AxiosInstance } from "axios"

import { Nullable } from "src/types"
import { sendToParent } from "../../services/embed-service"
import eventBus from "../../events/common-event-bus"
import { getToken, handleTokenExpired, recordRefreshFailure } from "../embed/useEmbedAuth"
import asclepiusConfig from "../../config/asclepius-config"
import asclepiusApiEvents from "./asclepius-api-events"

declare module "axios" {
  interface AxiosRequestConfig {
    _retry?: boolean
    _usesEmbedAuth?: boolean
    _embedRetried?: boolean
  }
}

export type AsclepiusAuthorizationScheme = "JWT" | "Bearer"

type AuthenticatedAsclepiusAxiosOptions = {
  authorizationScheme?: AsclepiusAuthorizationScheme
}

export function createAuthenticatedAsclepiusAxiosInstance(
  options: AuthenticatedAsclepiusAxiosOptions = {}
): AxiosInstance {
  const { authorizationScheme = "JWT" } = options
  const serverUrl = asclepiusConfig.getAsclepiusServerUrl()
  let refreshAccessTokenPromise: Promise<void> | null = null
  const axiosInstance: AxiosInstance = axios.create({
    baseURL: serverUrl,
    headers: { "Content-Type": "application/json" }
  })

  axiosInstance.interceptors.request.use(
    (request) => {
      const requestUrl: string = request.url ?? ""
      const callingAnAuthenticationApiIn: boolean =
        requestUrl.includes("auth/standard") ||
        requestUrl.includes("auth/password") ||
        requestUrl.includes("auth/passwordless") ||
        requestUrl.includes("auth/demographic") ||
        requestUrl.includes("auth/renewal") ||
        requestUrl.includes("auth/refresh")

      // Embed mode: Bearer token takes precedence.
      // Persist the decision on the request so the response interceptor
      // uses the same auth path regardless of token state changes mid-flight.
      const embedToken = getToken()
      if (embedToken && !callingAnAuthenticationApiIn) {
        request.headers["Authorization"] = `Bearer ${embedToken}`
        request._usesEmbedAuth = true
        return request
      }

      const accessToken: Nullable<string> = asclepiusConfig.getAccessToken()
      if (accessToken && !callingAnAuthenticationApiIn) {
        console.debug(`[Axios/Request/onFulfilled] Injecting access token into request… ${requestUrl}`)
        request.headers["Authorization"] = `${authorizationScheme} ${accessToken}`
      }

      return request
    },
    (error) => Promise.reject(error)
  )

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error.config) return Promise.reject(error)

      const originalRequestUrl: string = error.config.url ?? ""
      const originalRequest = error.config
      const status = error.response?.status
      console.debug(`[Axios/Response/onRejected] Status ${status} - ${originalRequestUrl}`)

      // --- Embed mode: handle 401 and 403 ---
      if (originalRequest._usesEmbedAuth) {
        if (status === 401) {
          if (originalRequest._embedRetried) {
            recordRefreshFailure()
            sendToParent("AUTH_ERROR", {
              code: "TOKEN_INVALID",
              message: "Authentication failed after refresh"
            })
            return Promise.reject(error)
          }
          try {
            const newToken = await handleTokenExpired()
            originalRequest._embedRetried = true
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`
            return axiosInstance(originalRequest)
          } catch (refreshError) {
            return Promise.reject(refreshError || error)
          }
        }

        if (status === 403) {
          if (originalRequest._embedRetried) {
            recordRefreshFailure()
            sendToParent("AUTH_ERROR", {
              code: "FORBIDDEN",
              message: "Access denied"
            })
            return Promise.reject(error)
          }
          try {
            const newToken = await handleTokenExpired("permission_denied")
            originalRequest._embedRetried = true
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`
            return axiosInstance(originalRequest)
          } catch (refreshError) {
            return Promise.reject(refreshError || error)
          }
        }

        return Promise.reject(error)
      }

      const refreshToken: Nullable<string> = asclepiusConfig.getRefreshToken()
      const isRequestRetriable = !originalRequest._retry
      const isTokenExpired = error.response?.data.app_error_code === AsclepiusAppErrorCodeEnum.InvalidAccessToken

      if (refreshToken && isRequestRetriable && isTokenExpired) {
        console.debug(`[Axios/Response/onRejected] Expired access token. Attempting to refresh… ${originalRequestUrl}`)
        originalRequest._retry = true

        if (refreshAccessTokenPromise) {
          await refreshAccessTokenPromise
          return axiosInstance(originalRequest)
        }

        try {
          refreshAccessTokenPromise = (async () => {
            const appInfo = asclepiusConfig.getAppInfo()
            const refreshedSession = (
              await new AuthenticationApi(undefined, serverUrl).refreshSession({
                refreshSessionRequest: {
                  refresh_token: refreshToken,
                  app_name: (appInfo?.appName as AppNameEnum) ?? null,
                  app_version: appInfo?.appVersion ?? null
                }
              })
            ).data
            const newAccessToken = refreshedSession.access_token
            const newRefreshToken = refreshedSession.refresh_token

            asclepiusConfig.setAccessToken(newAccessToken)
            asclepiusConfig.setRefreshToken(newRefreshToken)
            eventBus.$emit(asclepiusApiEvents.ACCESS_TOKEN_REFRESHED, { newAccessToken, newRefreshToken })
            originalRequest.headers["Authorization"] = `${authorizationScheme} ${newAccessToken}`
            axiosInstance.defaults.headers.common["Authorization"] = `${authorizationScheme} ${newAccessToken}`
            console.debug("[Axios/Response/onRejected] Successfully refreshed access token.")
          })()

          await refreshAccessTokenPromise
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          const refreshErrorCode = axios.isAxiosError(refreshError)
            ? refreshError.response?.data?.app_error_code
            : undefined
          const isTerminalRejection =
            refreshErrorCode === AsclepiusAppErrorCodeEnum.InvalidRefreshToken ||
            refreshErrorCode === AsclepiusAppErrorCodeEnum.UserDisabled

          if (isTerminalRejection) {
            console.debug("[Axios/Response/onRejected] Refresh session rejected… need to login again")
            asclepiusConfig.clearAuthTokens()
            eventBus.$emit(asclepiusApiEvents.UNAUTHENTICATED_API_ERROR_RESPONSE_RECEIVED)
            window.location.reload()
          }
          return Promise.reject(refreshError)
        } finally {
          refreshAccessTokenPromise = null
        }
      }

      if (status === 403 && error.response?.data.app_error_code === AsclepiusAppErrorCodeEnum.StaleSession) {
        asclepiusConfig.clearAuthTokens()
        eventBus.$emit(asclepiusApiEvents.UNAUTHENTICATED_API_ERROR_RESPONSE_RECEIVED)
        window.location.reload()
        return Promise.reject(error)
      }

      return Promise.reject(error)
    }
  )

  return axiosInstance
}
